import React from "react";
import { Box, Button, Container, Link, Typography, Sheet } from "@mui/joy";
import { NavLink } from "react-router-dom";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Notifications, People, Speed, Lock, Favorite, PhoneIphone } from "@mui/icons-material";
import { ColorSchemeToggle } from "../ColorSchemeToggle/index";
import Image from "../TwoSidedLayout/adam&eve.png";

// Feature card component
const FeatureCard = ({
    icon,
    title,
    description,
    delay,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    delay: number;
}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            p: 3,
            borderRadius: "xl",
            bgcolor: "background.surface",
            boxShadow: "sm",
            transition: "all 0.3s ease",
            animation: `fadeInUp 0.6s ease-out ${delay}s both`,
            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "lg",
            },
            "@keyframes fadeInUp": {
                from: { opacity: 0, transform: "translateY(20px)" },
                to: { opacity: 1, transform: "translateY(0)" },
            },
        }}
    >
        <Box
            sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "primary.softBg",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                color: "primary.600",
            }}
        >
            {icon}
        </Box>
        <Typography level="title-lg" sx={{ mb: 1 }}>
            {title}
        </Typography>
        <Typography level="body-md" textColor="text.secondary">
            {description}
        </Typography>
    </Box>
);

// Step component for How It Works
const Step = ({
    number,
    title,
    description,
    delay,
}: {
    number: number;
    title: string;
    description: string;
    delay: number;
}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            flex: 1,
            px: 2,
            animation: `fadeInUp 0.6s ease-out ${delay}s both`,
            "@keyframes fadeInUp": {
                from: { opacity: 0, transform: "translateY(20px)" },
                to: { opacity: 1, transform: "translateY(0)" },
            },
        }}
    >
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "primary.500",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
                fontWeight: "bold",
                mb: 2,
            }}
        >
            {number}
        </Box>
        <Typography level="title-lg" sx={{ mb: 1 }}>
            {title}
        </Typography>
        <Typography level="body-md" textColor="text.secondary">
            {description}
        </Typography>
    </Box>
);

export const Home = () => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                overflowX: "hidden",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.08) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(66, 171, 155, 0.05) 0%, transparent 50%)"
                        : "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.12) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(230, 242, 239, 0.8) 0%, transparent 50%)",
            }}
        >
            {/* Navigation */}
            <Sheet
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backdropFilter: "blur(12px)",
                    bgcolor: "rgba(255, 255, 255, 0.7)",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "[data-joy-color-scheme='dark'] &": {
                        bgcolor: "rgba(0, 0, 0, 0.7)",
                    },
                }}
            >
                <Typography
                    level="h4"
                    sx={{
                        fontWeight: 800,
                        background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    🦩 Stork
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ColorSchemeToggle />
                    <Button variant="outlined" component={NavLink} to="/signin" size="sm">
                        Sign In
                    </Button>
                    <Button component={NavLink} to="/signup" size="sm">
                        Get Started
                    </Button>
                </Box>
            </Sheet>

            {/* Hero Section */}
            <Box
                sx={{
                    position: "relative",
                    pb: { xs: 6, md: 8 },
                }}
            >
                <Container
                    sx={{
                        pt: { xs: 12, md: 14 },
                        pb: { xs: 4, md: 6 },
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "center",
                            gap: { xs: 4, md: 8 },
                            width: "100%",
                        }}
                    >
                        {/* Left content */}
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                textAlign: { xs: "center", md: "left" },
                                alignItems: { xs: "center", md: "flex-start" },
                            }}
                        >
                            <Typography
                                color="primary"
                                fontWeight="lg"
                                sx={{
                                    animation: "fadeInUp 0.6s ease-out 0.1s both",
                                    "@keyframes fadeInUp": {
                                        from: { opacity: 0, transform: "translateY(20px)" },
                                        to: { opacity: 1, transform: "translateY(0)" },
                                    },
                                }}
                            >
                                Share Your{" "}
                                <span
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    style={{ cursor: "default" }}
                                >
                                    {isHovered ? "Journie ❤️" : "Journey"}
                                </span>
                            </Typography>

                            <Typography
                                level="h1"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "clamp(2.5rem, 5vw, 4rem)",
                                    lineHeight: 1.1,
                                    animation: "fadeInUp 0.6s ease-out 0.2s both",
                                }}
                            >
                                Live Updates from the{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                    }}
                                >
                                    Delivery Room
                                </Box>
                            </Typography>

                            <Typography
                                level="body-lg"
                                textColor="text.secondary"
                                sx={{
                                    maxWidth: 480,
                                    lineHeight: 1.7,
                                    animation: "fadeInUp 0.6s ease-out 0.3s both",
                                }}
                            >
                                Keep friends and family in the loop during your most special moments. Share every
                                milestone of labor and delivery—without the hassle of individual texts.
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2,
                                    mt: 2,
                                    animation: "fadeInUp 0.6s ease-out 0.4s both",
                                }}
                            >
                                <Button
                                    size="lg"
                                    endDecorator={<ArrowForward />}
                                    component={NavLink}
                                    to="/signup"
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        fontSize: "1.1rem",
                                        boxShadow: "0 8px 24px rgba(66, 171, 155, 0.3)",
                                        "&:hover": {
                                            boxShadow: "0 12px 32px rgba(66, 171, 155, 0.4)",
                                        },
                                    }}
                                >
                                    Get Started Free
                                </Button>
                                <Button variant="outlined" size="lg" component={NavLink} to="/signin" sx={{ px: 4 }}>
                                    Sign In
                                </Button>
                            </Box>

                            <Typography
                                level="body-sm"
                                textColor="text.tertiary"
                                sx={{ animation: "fadeInUp 0.6s ease-out 0.5s both" }}
                            >
                                ✓ Free to use &nbsp; ✓ No app download required for guests
                            </Typography>
                        </Box>

                        {/* Right image */}
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                animation: "fadeInRight 0.8s ease-out 0.3s both",
                                "@keyframes fadeInRight": {
                                    from: { opacity: 0, transform: "translateX(40px)" },
                                    to: { opacity: 1, transform: "translateX(0)" },
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    borderRadius: "24px",
                                    overflow: "hidden",
                                    boxShadow: "0 32px 64px rgba(0, 0, 0, 0.15)",
                                    maxWidth: 500,
                                    width: "100%",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: "24px",
                                        padding: "2px",
                                        background:
                                            "linear-gradient(135deg, rgba(66, 171, 155, 0.5), rgba(50, 141, 127, 0.2))",
                                        WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                        WebkitMaskComposite: "xor",
                                        maskComposite: "exclude",
                                        pointerEvents: "none",
                                    },
                                }}
                            >
                                <img
                                    src={Image}
                                    alt="Happy family"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        display: "block",
                                        objectFit: "cover",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* How It Works Section */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                }}
            >
                <Container>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: "center",
                            mb: 2,
                            fontWeight: 700,
                        }}
                    >
                        How It Works
                    </Typography>
                    <Typography
                        level="body-lg"
                        textColor="text.secondary"
                        sx={{ textAlign: "center", mb: 6, maxWidth: 600, mx: "auto" }}
                    >
                        Three simple steps to keep everyone in the loop
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 4,
                            position: "relative",
                        }}
                    >
                        {/* Connecting line */}
                        <Box
                            sx={{
                                display: { xs: "none", md: "block" },
                                position: "absolute",
                                top: 24,
                                left: "20%",
                                right: "20%",
                                height: 2,
                                bgcolor: "primary.200",
                                zIndex: 0,
                            }}
                        />

                        <Step
                            number={1}
                            title="Create Your Room"
                            description="Set up a private room for your delivery journey in seconds"
                            delay={0.2}
                        />
                        <Step
                            number={2}
                            title="Share the Link"
                            description="Send your unique room link to friends and family via text or email"
                            delay={0.4}
                        />
                        <Step
                            number={3}
                            title="Post Updates"
                            description="Share milestones as they happen—everyone sees updates in real-time"
                            delay={0.6}
                        />
                    </Box>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: { xs: 8, md: 12 } }}>
                <Container>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: "center",
                            mb: 2,
                            fontWeight: 700,
                        }}
                    >
                        Why Families Love Stork
                    </Typography>
                    <Typography
                        level="body-lg"
                        textColor="text.secondary"
                        sx={{ textAlign: "center", mb: 6, maxWidth: 600, mx: "auto" }}
                    >
                        Focus on what matters most—we'll handle keeping everyone updated
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                            gap: 3,
                        }}
                    >
                        <FeatureCard
                            icon={<Speed sx={{ fontSize: 28 }} />}
                            title="Real-Time Updates"
                            description="No refreshing needed. Updates appear instantly for everyone watching."
                            delay={0.1}
                        />
                        <FeatureCard
                            icon={<PhoneIphone sx={{ fontSize: 28 }} />}
                            title="No App Required"
                            description="Guests simply click a link—no downloads, no accounts needed."
                            delay={0.2}
                        />
                        <FeatureCard
                            icon={<People sx={{ fontSize: 28 }} />}
                            title="Unlimited Viewers"
                            description="Share with as many people as you want. The more the merrier!"
                            delay={0.3}
                        />
                        <FeatureCard
                            icon={<Lock sx={{ fontSize: 28 }} />}
                            title="Private & Secure"
                            description="Only people with your link can see updates. Your privacy is our priority."
                            delay={0.4}
                        />
                        <FeatureCard
                            icon={<Notifications sx={{ fontSize: 28 }} />}
                            title="Text Notifications"
                            description="Guests can opt-in for SMS alerts so they never miss a moment."
                            delay={0.5}
                        />
                        <FeatureCard
                            icon={<Favorite sx={{ fontSize: 28 }} />}
                            title="Made with Love"
                            description="Built by parents, for parents. We understand these precious moments."
                            delay={0.6}
                        />
                    </Box>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: "absolute",
                        width: 300,
                        height: 300,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.1)",
                        top: -100,
                        right: -50,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.05)",
                        bottom: -50,
                        left: "20%",
                    }}
                />

                <Container sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
                    <Typography level="h2" sx={{ color: "white", mb: 2, fontWeight: 700 }}>
                        Ready to Share Your Journey?
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.9)", mb: 4, maxWidth: 500, mx: "auto" }}>
                        Join thousands of families who've used Stork to share their special moments
                    </Typography>
                    <Button
                        size="lg"
                        variant="solid"
                        component={NavLink}
                        to="/signup"
                        sx={{
                            bgcolor: "white",
                            color: "primary.600",
                            px: 5,
                            py: 1.5,
                            fontSize: "1.1rem",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,0.9)",
                            },
                        }}
                        endDecorator={<ArrowForward />}
                    >
                        Get Started Free
                    </Button>
                </Container>
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    py: 4,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                }}
            >
                <Container>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <Typography level="body-sm" textColor="text.secondary">
                            Built by{" "}
                            <Link
                                href="https://bnuckols.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                textColor="primary.500"
                            >
                                Brennon
                            </Link>
                        </Typography>
                        <Box sx={{ display: "flex", gap: 3 }}>
                            <Link level="body-sm" textColor="text.secondary" component={NavLink} to="/signin">
                                Sign In
                            </Link>
                            <Link level="body-sm" textColor="text.secondary" component={NavLink} to="/signup">
                                Sign Up
                            </Link>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};
